import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch, ExternalLink, ChevronRight } from "lucide-react";
import { http } from "../lib/api";

/**
 * AcrossTheAisle — surfaces the same story as covered from opposite lanes.
 * Newseze signature feature: shows readers how the same news is framed by
 * Conservative, Left, and Center outlets, side by side.
 *
 * Renders nothing when no cross-coverage is found (e.g. sports, tech),
 * which is intentional — silence is honest when no lane has an opposing take.
 */
const LANE_STYLES = {
  conservative: { label: "Conservative take", color: "#7c2d12", bg: "bg-rose-50", border: "border-rose-200" },
  left: { label: "Left take", color: "#1e3a8a", bg: "bg-sky-50", border: "border-sky-200" },
  center: { label: "Center take", color: "#0e2d63", bg: "bg-slate-50", border: "border-slate-200" },
};

export default function AcrossTheAisle({ articleId }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (alive) setLoading(true);
      try {
        const r = await http.get(`/news/across-aisle/${articleId}`);
        if (!alive) return;
        setMatches(r.data?.matches || []);
      } catch {
        if (alive) setMatches([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [articleId]);

  if (loading || matches.length === 0) return null;

  return (
    <section className="rounded-xl border border-[var(--gold)] bg-[#fff9ed] p-5 mt-6 mb-6" data-testid="across-aisle">
      <div className="flex items-center gap-2 mb-3">
        <GitBranch size={14} className="text-[#7a5b00]" />
        <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[#7a5b00]">Across the aisle</h3>
        <span className="text-[11px] text-[var(--muted)]">Same story · other lanes</span>
      </div>
      <p className="text-[12.5px] text-[var(--muted)] mb-3 max-w-[640px] leading-snug">
        Here&apos;s how the same story is being covered by outlets in other lanes. Read both — Newseze doesn&apos;t pick a side.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="across-aisle-grid">
        {matches.map((m) => {
          const style = LANE_STYLES[m.across_aisle_lane] || LANE_STYLES.center;
          return (
            <Link
              key={m.id}
              to={`/article/${m.id}`}
              className={`group ${style.bg} border ${style.border} rounded-md p-3 hover:shadow-md hover:-translate-y-0.5 transition flex flex-col gap-1.5`}
              data-testid={`across-aisle-${m.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.16em] font-black" style={{ color: style.color }}>
                  {style.label}
                </span>
                <ChevronRight size={12} className="text-[var(--muted)] opacity-60 group-hover:opacity-100" />
              </div>
              <div className="text-[13px] font-black leading-snug text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-3">
                {m.title}
              </div>
              <div className="flex items-center justify-between text-[10px] text-[var(--muted)] uppercase tracking-[0.12em] font-bold opacity-70 mt-auto">
                <span>Wire: {m.source}</span>
                {typeof m.trust === "number" && m.trust > 0 && (
                  <span className="text-[var(--blue)]">Trust {m.trust}</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-[var(--muted)] italic flex items-center gap-1.5">
        <ExternalLink size={10} /> All lanes still pass Newseze&apos;s calm filters (no drama, no conspiracy, respect baseline).
      </div>
    </section>
  );
}
